import { getIntegrationManifest, personalChannels } from "#features/conversations/integrations/index.ts";
import { IconAssets } from "#types/assets.ts";
import type { ConnectorChannelType, ConnectorType, ConversationSource, DataSource, RemoteMcpItemCustomProperties } from "#types/types.ts";

export interface ConnectorChannel {
    nameKey: string;
    iconUrl: IconAssets;
    iconUrlAlt: IconAssets;
    iconUrlDark?: IconAssets;
    iconUrlAltDark?: IconAssets;
    connectorType?: ConnectorType;
}

// Channel types with no manifest — legacy/native connectors and the file-crawler-style web
// channel. Everything integration-specific (Slack included) comes from INTEGRATION_MANIFESTS
// via `personalChannels` below; adding a new integration never touches this object.
const nativeChannels = {
    WorkleapOfficevibe: {
        nameKey: "channels.officevibe",
        iconUrl: IconAssets.Officevibe,
        iconUrlAlt: IconAssets.OfficevibeAlt,
        connectorType: "Workleap"
    },
    // TODO WLAI-5711: Remove once SharePoint documents are fully cleaned up from the database
    Sharepoint: {
        nameKey: "channels.sharepoint",
        iconUrl: IconAssets.Sharepoint,
        iconUrlAlt: IconAssets.SharepointAlt
    },
    WorkleapFileLibrary: {
        nameKey: "channels.fileLibrary",
        iconUrl: IconAssets.FileLibrary,
        iconUrlAlt: IconAssets.FileLibraryAlt,
        connectorType: "Workleap"
    },
    WorkleapLms: {
        nameKey: "channels.lms",
        iconUrl: IconAssets.Lms,
        iconUrlAlt: IconAssets.LmsAlt,
        connectorType: "Workleap"
    },
    WorkleapPingboard: {
        nameKey: "channels.pingboard",
        iconUrl: IconAssets.Workleap,
        iconUrlAlt: IconAssets.Workleap,
        connectorType: "Workleap"
    },
    WorkleapPlatform: {
        nameKey: "channels.platform",
        iconUrl: IconAssets.Workleap,
        iconUrlAlt: IconAssets.Workleap,
        connectorType: "Workleap"
    },
    WorkleapSkills: {
        nameKey: "channels.skills",
        iconUrl: IconAssets.Skills,
        iconUrlAlt: IconAssets.Skills,
        connectorType: "Workleap"
    },
    WorkleapPerformance: {
        nameKey: "channels.performance",
        iconUrl: IconAssets.Performance,
        iconUrlAlt: IconAssets.PerformanceAlt,
        connectorType: "Workleap"
    },
    WebCrawler: {
        nameKey: "channels.webCrawler",
        iconUrl: IconAssets.WebPage,
        iconUrlAlt: IconAssets.WebPageAlt,
        connectorType: "WebCrawler"
    },
    // Fallback for remote-MCP sources that carry no integration key, either because
    // they were persisted before the key existed or because the vendor is unmapped.
    UserRemoteMcp: {
        nameKey: "channels.remoteMcp",
        iconUrl: IconAssets.WorkleapAi,
        iconUrlAlt: IconAssets.WorkleapAi
    },
    None: {
        // `channels.none` was never a key in either locale file, so this footer rendered the raw
        // key. `channels.unknown` is the translated entry that was always meant for it.
        nameKey: "channels.unknown",
        iconUrl: IconAssets.Workleap,
        iconUrlAlt: IconAssets.Workleap
    }
} satisfies Partial<Record<ConnectorChannelType, ConnectorChannel>>;

// Google citations carry the product they come from (an email, a file, an event), the way Atlassian's
// `itemKindChannels` do for Rovo; the single Google entry stays the connection's, in `personalChannels`.
export const googleProductChannels = {
    gmail: { nameKey: "channels.gmail", iconUrl: IconAssets.Gmail, iconUrlAlt: IconAssets.GmailAlt },
    drive: { nameKey: "channels.googleDrive", iconUrl: IconAssets.GoogleDrive, iconUrlAlt: IconAssets.GoogleDriveAlt },
    calendar: { nameKey: "channels.googleCalendar", iconUrl: IconAssets.GoogleCalendar, iconUrlAlt: IconAssets.GoogleCalendarAlt }
} satisfies Record<string, ConnectorChannel>;

// `nativeChannels` plus `personalChannels` together cover every ConnectorChannelType the backend
// still emits — enforced by index.test.ts's manifest-exhaustiveness test and channels.test.ts below.
// `Confluence`/`Teams` are gone: the backend only ever emits their `UserConfluence`/`UserTeams` alias now.
export const channels = { ...nativeChannels, ...personalChannels } as Record<ConnectorChannelType, ConnectorChannel>;

// TODO: Revisit the "table" DataSource as we might not guarantee the data is coming from OV
export const sourceTypeChannels: Record<DataSource, ConnectorChannel> = {
    confluence_page: channels.UserConfluence,
    slack_message: channels.Slack,
    workleap_officevibe_engagement_score: channels.WorkleapOfficevibe,
    workleap_officevibe_feedback: channels.WorkleapOfficevibe,
    workleap_officevibe_oneonone_agenda: channels.WorkleapOfficevibe,
    workleap_officevibe_recognition: channels.WorkleapOfficevibe,
    workleap_files_library_file: channels.WorkleapFileLibrary,
    sharepoint_page: channels.UserSharepoint,
    sharepoint_file: channels.UserSharepoint,
    workleap_lms_course: channels.WorkleapLms,
    workleap_lms_enrollment: channels.WorkleapLms,
    web_page: channels.WebCrawler,
    workleap_performance_management_goal: channels.WorkleapPerformance,
    workleap_performance_management_plan: channels.WorkleapPerformance,
    workleap_performance_management_review: channels.WorkleapPerformance,
    workleap_performance_management_review_cycle: channels.WorkleapPerformance,
    workleap_performance_management_peer_review_nomination: channels.WorkleapPerformance,
    jira_issue: channels.UserJira,
    unknown: channels.None,
    notion_page: channels.UserNotionMcp,
    outlook_calendar_event: channels.UserOutlook,
    outlook_message: channels.UserOutlook,
    teams_message: channels.UserTeams,
    table: channels.WorkleapOfficevibe,
    google_drive_file: googleProductChannels.drive,
    google_calendar_event: googleProductChannels.calendar,
    gmail_message: googleProductChannels.gmail,
    remote_mcp_item: channels.UserRemoteMcp,
    workleap_app_meeting: channels.WorkleapPlatform,
    teams_meeting_transcript: channels.UserTeams
};

/**
 * Resolves the channel for a source type, falling back to the neutral `None`
 * channel for source types the frontend hasn't been regenerated for yet
 * (e.g. a new backend DataSource deployed ahead of the frontend). Keeps the
 * unmapped-source hardening in one place instead of at each preview call site.
 */
export const getSourceTypeChannel = (sourceType: DataSource): ConnectorChannel =>
    sourceTypeChannels[sourceType] ?? channels.None;

export const getConversationSourceChannel = (source: ConversationSource): ConnectorChannel => {
    if (source.sourceType === "remote_mcp_item") {
        const { kind, integrationKey } = source.customProperties as RemoteMcpItemCustomProperties;

        // "unknown", a missing key, and a key the frontend hasn't shipped a manifest for yet
        // (e.g. a backend deployed ahead of the frontend) all fall through to the neutral channel below.
        const manifest = integrationKey ? getIntegrationManifest(integrationKey) : undefined;

        // A vendor whose citations carry more than one product kind (Atlassian Rovo: Jira, Confluence,
        // Loom) declares its per-kind logo on its own manifest — checked ahead of that manifest's
        // single `channelType` icon, itself checked ahead of the neutral channel below.
        const itemKindChannel = kind ? manifest?.itemKindChannels?.[kind] : undefined;

        if (itemKindChannel) {
            return itemKindChannel;
        }

        if (manifest) {
            return channels[manifest.channelType];
        }
    }

    return getSourceTypeChannel(source.sourceType);
};
